import {
	ConversionException,
	ConversionInvoiceExportSummaryRow,
	ConversionMigrationResult,
	ConversionSkippedInvoice,
} from './models/ConversionMigrationResult';
import { ConversionClient } from './models/ConversionClient';

/** Negative invoices the conversion skipped, across the given results. */
export function skippedNegativeInvoices(results: ConversionMigrationResult[]): ConversionSkippedInvoice[] {
	return (results || []).reduce(
		(all, r) => all.concat((r && r.skippedNegativeInvoices) || []),
		[] as ConversionSkippedInvoice[]
	);
}

/**
 * Downloads the negative invoices the conversion skipped as a CSV, for the account rep.
 * namePart goes in the file name (a carrier name, or "all_carriers").
 */
export function downloadSkippedInvoicesCsv(invoices: ConversionSkippedInvoice[], namePart: string) {
	if (!invoices || invoices.length === 0) {
		return;
	}

	const header = ['Carrier', 'Client #', 'Invoice #', 'PO #', 'Debtor', 'Invoice Date', 'Due Date', 'Invoice Amount', 'Balance', 'Reason'];
	const csvValue = (value: any) => {
		const text = value === null || value === undefined ? '' : value.toString();
		return /[",\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
	};
	const dateValue = (value: string) => (value ? value.substring(0, 10) : '');
	const rows = invoices.map((x) =>
		[
			x.clientName,
			x.clientNo,
			x.invoiceNo,
			x.purchaseOrder,
			x.debtorName,
			dateValue(x.invoiceDate),
			dateValue(x.dueDate),
			x.invoiceAmount,
			x.balance,
			x.reason,
		]
			.map(csvValue)
			.join(',')
	);
	const csv = [header.join(',')].concat(rows).join('\r\n');

	const today = new Date();
	const stamp =
		today.getFullYear() + '-' + ('0' + (today.getMonth() + 1)).slice(-2) + '-' + ('0' + today.getDate()).slice(-2);
	const safeName = (namePart || 'carrier').replace(/[^A-Za-z0-9]+/g, '_');
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = 'Negative_Invoices_' + safeName + '_' + stamp + '.csv';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(link.href);
}

/** Exceptions (data problems the conversion worked around, e.g. invalid EINs) across the given results. */
export function conversionExceptions(results: ConversionMigrationResult[]): ConversionException[] {
	return (results || []).reduce(
		(all, r) => all.concat((r && r.exceptions) || []),
		[] as ConversionException[]
	);
}

/**
 * Downloads the conversion grid as a CSV: the carriers it shows (the current tab and filters), with the grid's columns
 * and the status text the grid shows. namePart goes in the file name.
 */
export function downloadConversionListCsv(rows: ConversionClient[], status: (row: ConversionClient) => string, namePart: string) {
	if (!rows || rows.length === 0) {
		return;
	}

	const dateValue = (value: string) =>
		value && value.length >= 10 ? value.substring(5, 7) + '/' + value.substring(8, 10) + '/' + value.substring(0, 4) : '';
	const header = [
		'Carrier Name',
		'Client #',
		'WinFactor ID',
		'Term',
		'Brokers',
		'Missing in FC',
		'Invoices',
		'Open Invoices',
		'Open Balance',
		'Status',
		'Part 1 Completed',
		'Converted',
	];
	const lines = rows.map((x) =>
		[
			x.clientName,
			x.clientNos,
			x.winFactorIds,
			x.termName,
			x.debtorCount,
			x.missingDebtorCount,
			x.invoiceCount,
			x.openInvoiceCount,
			(x.openInvoiceBalance || 0).toFixed(2),
			status(x),
			dateValue(x.conversionPart1CompletedDate),
			dateValue(x.conversionCompletedDate),
		]
			.map(csvCell)
			.join(',')
	);
	saveCsv([header.join(',')].concat(lines).join('\r\n'), 'Conversion_List_', namePart);
}

/** Downloads the conversion exceptions as a CSV. namePart goes in the file name. */
export function downloadExceptionsCsv(exceptions: ConversionException[], namePart: string) {
	if (!exceptions || exceptions.length === 0) {
		return;
	}

	const header = ['Carrier', 'Client #', 'WinFactor ID', 'Issue', 'Value', 'Action'];
	const rows = exceptions.map((x) =>
		[x.clientName, x.clientNo, x.winFactorId, x.issue, x.value, x.action].map(csvCell).join(',')
	);
	saveCsv([header.join(',')].concat(rows).join('\r\n'), 'Conversion_Exceptions_', namePart);
}

/** The invoice summary report lines (step 12) across the given results. */
export function invoiceExportSummaryRows(results: ConversionMigrationResult[]): ConversionInvoiceExportSummaryRow[] {
	return (results || []).reduce(
		(all, r) => all.concat((r && r.invoiceExportSummary) || []),
		[] as ConversionInvoiceExportSummaryRow[]
	);
}

/**
 * Downloads the invoice summary report for a full conversion: the same report Create Invoice Export Files saves, one line
 * per client with its invoices summed across every export file, then a TOTAL line.
 */
export function downloadInvoiceExportSummaryCsv(lines: ConversionInvoiceExportSummaryRow[], namePart: string) {
	if (!lines || lines.length === 0) {
		return;
	}

	const sorted = lines.slice().sort((a, b) => (a.clientName || '').localeCompare(b.clientName || ''));
	const dateValue = (value: string) =>
		value && value.length >= 10 ? value.substring(5, 7) + '/' + value.substring(8, 10) + '/' + value.substring(0, 4) : '';
	const money = (value: number) => (value || 0).toFixed(2);
	const sum = (pick: (x: ConversionInvoiceExportSummaryRow) => number) => sorted.reduce((total, x) => total + (pick(x) || 0), 0);
	const dates = (pick: (x: ConversionInvoiceExportSummaryRow) => string) =>
		sorted
			.map(pick)
			.filter((x) => !!x)
			.sort();
	const files: string[] = [];
	sorted.forEach((x) =>
		(x.exportFiles || []).forEach((file) => {
			if (files.indexOf(file) < 0) {
				files.push(file);
			}
		})
	);

	const header = ['ClientCode', 'ClientName', 'InvoicesToImport', 'AmountToImport', 'Debtors', 'EarliestInvoiceDate', 'LatestInvoiceDate', 'ExportFiles', 'InvoicesNotSent', 'AmountNotSent', 'InvoicesNotWritten'];
	const rows: any[][] = sorted.map((x) => [
		x.clientCode,
		x.clientName,
		x.invoicesToImport,
		money(x.amountToImport),
		x.debtors,
		dateValue(x.earliestInvoiceDate),
		dateValue(x.latestInvoiceDate),
		(x.exportFiles || []).join('; '),
		x.invoicesNotSent,
		money(x.amountNotSent),
		x.invoicesNotWritten,
	]);
	const earliest = dates((x) => x.earliestInvoiceDate);
	const latest = dates((x) => x.latestInvoiceDate);
	rows.push([
		'',
		'TOTAL (' + sorted.length + ' client(s))',
		sum((x) => x.invoicesToImport),
		money(sum((x) => x.amountToImport)),
		'',
		earliest.length > 0 ? dateValue(earliest[0]) : '',
		latest.length > 0 ? dateValue(latest[latest.length - 1]) : '',
		files.length + ' file(s)',
		sum((x) => x.invoicesNotSent),
		money(sum((x) => x.amountNotSent)),
		sum((x) => x.invoicesNotWritten),
	]);
	downloadCsv(header, rows, 'Invoice_Export_Summary_', namePart);
}

/** Downloads any rows as a CSV file named prefix + name + date. */
export function downloadCsv(header: string[], rows: any[][], prefix: string, namePart: string) {
	saveCsv([header.join(',')].concat(rows.map((row) => row.map(csvCell).join(','))).join('\r\n'), prefix, namePart);
}

function csvCell(value: any): string {
	const text = value === null || value === undefined ? '' : value.toString();
	return /[",\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
}

/** Saves a file the server sent (e.g. an invoice import error CSV) under its own name. */
export function saveDownloadedFile(blob: Blob, fileName: string) {
	const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv;charset=utf-8;' }));
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	// Released a moment later: revoking it straight after the click can cancel the download.
	setTimeout(() => {
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}, 1000);
}

/** The server's message from a failed file download (the body comes back as a Blob holding the text). */
export function readDownloadError(error: any, fallback: string): Promise<string> {
	const body = error ? error.error : null;
	const clean = (text: string) => {
		const trimmed = (text || '').trim().replace(/^"|"$/g, '');
		return trimmed || fallback;
	};
	if (body instanceof Blob) {
		return new Promise<string>((resolve) => {
			const reader = new FileReader();
			reader.onload = () => resolve(clean(reader.result as string));
			reader.onerror = () => resolve(fallback);
			reader.readAsText(body);
		});
	}
	return Promise.resolve(typeof body === 'string' ? clean(body) : fallback);
}

function saveCsv(csv: string, prefix: string, namePart: string) {
	const today = new Date();
	const stamp =
		today.getFullYear() + '-' + ('0' + (today.getMonth() + 1)).slice(-2) + '-' + ('0' + today.getDate()).slice(-2);
	const safeName = (namePart || 'carrier').replace(/[^A-Za-z0-9]+/g, '_');
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = prefix + safeName + '_' + stamp + '.csv';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(link.href);
}



// WEBPACK FOOTER //
// ./src/app/portal/conversion/skipped-invoices-csv.ts